import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ageRangeOptions, genderOptions, countryOption, CognitoResponseCode, profileCompletionPage } from 'src/app/shared/utilities/constants';
import { FormsModule, NgForm } from '@angular/forms';
import { AuthModule } from 'src/app/modules/auth/auth.module';
import { InputComponent } from 'src/app/shared/components/validation/validations.component';
import { ProfileService } from 'src/app/shared/services/profile.service';
import { AuthenticationService } from 'src/app/shared/services/authentication/authentication.service';
import { MsgService } from 'src/app/shared/services/msg.service';
import { UserProfileAttributeUpdateRequest } from 'src/app/shared/models/profile/profile';
import { firstValueFrom } from 'rxjs';
import { CommonService, tryCatch } from '@laasy/common-layout';
import { MissingInfoModalService } from 'src/app/shared/services/missing-info-modal.service';
import { formatPhoneNumber } from 'src/app/modules/auth/auth-utils';
import { ProfileCompletionService } from 'src/app/modules/auth/service/profile-completion.service';
import { RedirectPostLoginService } from 'src/app/modules/auth/service/redirect-post-login.service';

@Component({
  selector: 'app-profile-completion-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, InputComponent, AuthModule],
  templateUrl: './profile-completion-modal.component.html',
  styleUrls: ['./profile-completion-modal.component.scss']
})
export class ProfileCompletionModalComponent implements OnInit {

  get isFormValid(): boolean {
    return this.profileForm?.valid ?? false;
  }

  title: string = profileCompletionPage.profileCompletionModal.title;
  description: string = profileCompletionPage.profileCompletionModal.description;

  formData = {
    firstName: '',
    lastName: '',
    ageRange: '',
    gender: '',
    country: '',
    zipCode: '',
    phoneNumber: '',
    optInSmsSubscription: false
  };

  originalFormData = {
    firstName: '',
    lastName: '',
    ageRange: '',
    gender: '',
    country: '',
    zipCode: '',
    phoneNumber: '',
    optInSmsSubscription: false
  };

  ageRanges = ageRangeOptions.map(opt => ({
    name: opt.name,
    value: opt.value
  }));

  genders = genderOptions.map(opt => ({
    name: opt.name,
    value: opt.value
  }));

  countries = countryOption.map(opt => ({
    name: opt.name,
    value: opt.code
  }));

  isProfileCompleted: boolean = true;
  isSkipable: boolean = false;
  @ViewChild('profileForm') profileForm!: NgForm;
  @Output() action = new EventEmitter<string>();
  @Input() isNextModal: boolean = false;
  userProfile = {sms_opt_in: false};

  constructor(
    private profileService: ProfileService,
    private authService: AuthenticationService,
    private msgService: MsgService,
    private commonService: CommonService,
    private cdr: ChangeDetectorRef,
    private missingInfoModalService: MissingInfoModalService,
    private redirectPostLogin: RedirectPostLoginService,
    private profileCompletionService: ProfileCompletionService,
  ) { }

  async ngOnInit(): Promise<void> {
    this.isSkipable = this.missingInfoModalService.isSkipable;
    const res: any = await tryCatch(firstValueFrom(this.profileService.GetUserProfile()));

    if (res?.error) {
      this.msgService.error(profileCompletionPage.message.failedToLoadProfile);
      return;
    }

    this.initializeProfileAttributes(res.data.data.attributes);

    this.isProfileCompleted = Object.values(this.formData).every(value => value !== '');
    if (this.isProfileCompleted) {
      this.title = profileCompletionPage.improveExperienceModal.title;
      this.description = profileCompletionPage.improveExperienceModal.description;
    }

    this.originalFormData = { ...this.formData };
    this.cdr.detectChanges();
  }

  private initializeProfileAttributes(attributes: any[]): void {
    if(!attributes?.length) return;

    this.formData.firstName = attributes.find((attr: any) => attr.title === 'First name')?.value || '';
    this.formData.lastName = attributes.find((attr: any) => attr.title === 'Last name')?.value || '';
    this.formData.ageRange = attributes.find((attr: any) => attr.title === 'Age Range')?.value || '';
    this.formData.gender = attributes.find((attr: any) => attr.title === 'Gender')?.value || '';
    this.formData.country = attributes.find((attr: any) => attr.title === 'Country')?.value || '';
    this.formData.country = this.formData.country.trim();
    this.formData.zipCode = attributes.find((attr: any) => attr.title === 'Zip Code')?.value || '';
    this.formData.phoneNumber = attributes.find((attr: any) => attr.title === 'Phone number')?.value || '';
    this.formData.optInSmsSubscription = attributes.find((attr: any) => attr.title === 'Opt In Sms Subscription')?.value === 'True' || false;
    this.userProfile.sms_opt_in = this.formData.optInSmsSubscription;
  }

  async updateProfile(updateProfileRequest: UserProfileAttributeUpdateRequest): Promise<boolean> {
    if(updateProfileRequest.attribute == 'phoneNumber') {
      updateProfileRequest.value = updateProfileRequest.value.replace(/[^0-9]/g, '')  ;
    }
    const cognitoResponse = await this.authService.UpdateUserAttribute(updateProfileRequest);
    if (cognitoResponse == CognitoResponseCode.Success || cognitoResponse == 'Non-Cognito-Field') {
      const res: any = await tryCatch(firstValueFrom(this.profileService.UpdateUserProfile(updateProfileRequest)));
      if (res?.error) return false;
      return true;
    }
    return false;
  }

  async onSubmit(): Promise<void> {

    if (this.isProfileCompleted) {
      this.action.emit(profileCompletionPage.action.profileSubmitted);
      return;
    }
    if (!this.isFormValid) {
      this.msgService.error(profileCompletionPage.message.fillAllFields);
      return;
    }

    if(!this.isProfileCompleted)
      this.action.emit(profileCompletionPage.action.submitted);

    const updates = this.getProfileUpdates();
    if (updates.length === 0) {
      this.action.emit(profileCompletionPage.action.profileSubmitted);
      return;
    }

    try {
      const results = await Promise.all(updates.map(update => this.updateProfile(update)));
      const allSuccessful = results.every(result => result === true);

      if (allSuccessful) {
        this.originalFormData = { ...this.formData };
        if(!this.isNextModal) {
          this.msgService.success(profileCompletionPage.message.success);
          this.redirectPostLogin.redirectPostLogin(this.profileCompletionService.userEmail, {})
        }
        this.action.emit(profileCompletionPage.action.profileSubmitted);
      }
      else {
        this.msgService.error(profileCompletionPage.message.profileUpdateError);
      }
    } catch (error) {
      this.msgService.error(profileCompletionPage.message.profileUpdateError);
    }
  }

  private getProfileUpdates(): UserProfileAttributeUpdateRequest[] {
    const updates: UserProfileAttributeUpdateRequest[] = [];

    // Check each field for changes
    Object.keys(this.formData).forEach(key => {
      const currentValue = this.formData[key as keyof typeof this.formData];
      const originalValue = this.originalFormData[key as keyof typeof this.originalFormData];

      if (currentValue !== originalValue) {
        updates.push({
          attribute: key,
          value: currentValue.toString()
        });
      }
    });

    return updates;
  }

  onSkip(): void {
    this.action.emit(profileCompletionPage.action.skip);
  }

  icon: any;
  async setModalIcon(): Promise<void> {
    this.icon = await firstValueFrom(this.commonService.getSvgCode('assets/icons/tenantCustomIcons/userPref.svg#homeIcon'));
  }

  formatPhoneNumber(event: any) {
    this.formData.phoneNumber = formatPhoneNumber(event);
  }

  smsOptInChange($event: any) {
    this.formData.optInSmsSubscription = this.userProfile.sms_opt_in;
  }
}
